import { useReducer, useEffect, useCallback } from 'react'
import numeral from 'numeral'
import { Toaster } from '@ui'
import { debounce, get } from 'lodash'
import { ApolloError } from 'apollo-boost'
import api, { ISpaceDetails, ITag } from '@api'
import { useFloor } from '@providers/mapProvider'
import { seatable } from '@ui/canvas/utils'

type UpdateActionPayload = {
  [P in keyof ISpaceDetails]?: any
}
interface IUpdatedData {
  data: { updateSpace: { space: ISpaceDetails } }
}

enum Actions {
  LOAD,
  UPDATE_SPACE,
  UPDATE_DATA,
  RESET_DATA,
  UPDATE_SPACE_TYPE,
  UPDATE_USAGE_TYPE,
  UPDATE_SPACE_ASSIGNMENT,
}

interface LoadAction {
  type: Actions.LOAD
  payload: ISpaceDetails
}

interface UpdateAction {
  type: Actions.UPDATE_SPACE
}

interface UpdateData {
  type: Actions.UPDATE_DATA
  space: Partial<ISpaceDetails>
}

interface ResetData {
  type: Actions.RESET_DATA
}

export interface IState {
  isLoading: boolean
  isDirty: boolean
  space: ISpaceDetails | null
  cachedSpace: ISpaceDetails | null
}

type IActions = LoadAction | UpdateAction | UpdateData | ResetData

function reducer(state: IState, action: IActions): IState {
  switch (action.type) {
    case Actions.LOAD:
      return {
        isLoading: false,
        isDirty: false,
        space: action.payload,
        cachedSpace: action.payload,
      }
    case Actions.UPDATE_SPACE:
      if (state.space) {
        return {
          ...state,
          isLoading: false,
          isDirty: false,
        }
      } else {
        return state
      }
    case Actions.UPDATE_DATA: {
      return {
        ...state,
        isDirty: true,
        space: {
          ...(state.space as ISpaceDetails),
          ...action.space,
        },
      }
    }
    case Actions.RESET_DATA: {
      return {
        ...state,
        isDirty: false,
        space: state.cachedSpace,
      }
    }

    default:
      return state
  }
}

export default function useSpaceDetails(id: string, apId?: string) {
  const [data, dispatch] = useReducer(reducer, {
    isLoading: true,
    isDirty: false,
    space: null,
    cachedSpace: null,
  })
  const floor = useFloor()
  useEffect(() => {
    api.space
      .details(id, apId)
      .then((data) => dispatch({ type: Actions.LOAD, payload: data }))
      .catch((error: ApolloError) => Toaster.fail({ error }))
  }, [id, apId])

  const updateSpace = useCallback(
    debounce(
      (payload: UpdateActionPayload, usageType?: string | null) =>
        api.space
          .update(id, { id, ...payload })
          .then(() => {
            if (usageType && seatable(payload?.spaceType)) {
              updateUsageType(usageType)
            } else {
              floor?.reloadMap?.()
              dispatch({ type: Actions.UPDATE_SPACE })
              Toaster.success({ message: 'Successfully saved' })
            }
          })
          .catch((error: ApolloError) => Toaster.fail({ error })),
      300
    ),
    [id]
  )

  const updateUsageType = useCallback(
    (usageType: string) =>
      api.space
        .batchUpdateUsageTypes([id], usageType)
        .then((space) => {
          dispatch({ type: Actions.UPDATE_SPACE })
          Toaster.success({ message: 'Successfully saved' })
          floor.updateSpace?.(space)
        })
        .catch((error: ApolloError) => Toaster.fail({ error })),
    [id]
  )

  const updateData = (space: Partial<ISpaceDetails>) => {
    dispatch({ type: Actions.UPDATE_DATA, space })
  }

  const resetData = () => {
    dispatch({ type: Actions.RESET_DATA })
  }

  const update = (assignmentPlanId: string) => {
    const updateObj: any = {}
    let updatedUsageType = null
    if (data.cachedSpace?.capacity !== data.space?.capacity)
      updateObj.capacity = data.space?.capacity
    if (data.cachedSpace?.spaceType?.name !== data.space?.spaceType?.name)
      updateObj.spaceType = data.space?.spaceType.name
    if (
      data.cachedSpace?.usageType?.name !== data.space?.usageType?.name &&
      !updateObj.hasOwnProperty('spaceType')
    ) {
      updatedUsageType = data.space?.usageType?.name
      updateUsageType(updatedUsageType as string)
    }
    if (data.cachedSpace?.calendar?.id !== data.space?.calendar?.id) {
      updateObj.calendarId = data.space?.calendar?.id || null
    }
    updateSpace(
      {
        tagNames: data.space?.tags.map((tag: ITag) => tag.name),
        name: data.space?.name,
        comment: data.space?.comment,
        measurements: numeral(data.space?.measurements).value(),
        data,
        ...updateObj,
      },
      updatedUsageType
    )
    if (data.cachedSpace?.workplaceGroup?.name !== data.space?.workplaceGroup?.name) {
      if (!data.space?.workplaceGroup?.name) {
        api.workplaceGroup.removeSpaces([id])
      } else {
        if (data.space?.workplaceGroup?.id === '') {
          api.workplaceGroup.create({ name: data.space?.workplaceGroup?.name }).then((value) => {
            api.workplaceGroup.addSpaces(value.id, [id])
          })
        } else api.workplaceGroup.addSpaces(data.space?.workplaceGroup?.id, [id])
      }
    }

    if (data.cachedSpace?.department?.name !== data.space?.department?.name)
      api.space.allocate({
        toDepartmentId: get(data, 'space.department.id', null),
        assignmentPlanId: assignmentPlanId,
        spaceIds: [id],
      })
  }

  return {
    data,
    update,
    updateSpace,
    updateData,
    resetData,
    updateUsageType,
  }
}
